import { useState, useRef } from 'react';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  saveCatalogGroup,
  deleteCatalogGroup,
  type CatalogGroupItem,
} from '@/services/data-integration-api';

interface CatalogGroupPanelProps {
  connectionId: string;
  groups: CatalogGroupItem[];
  onGroupsChange: () => void;
}

export default function CatalogGroupPanel({
  connectionId,
  groups,
  onGroupsChange,
}: CatalogGroupPanelProps) {
  const [newGroupName, setNewGroupName] = useState('');
  const [adding, setAdding] = useState(false);

  // inline rename state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // inline sort_order edit state
  const [editingSortId, setEditingSortId] = useState<string | null>(null);
  const [editingSortValue, setEditingSortValue] = useState<number>(0);

  // delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState<CatalogGroupItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Add group ───────────────────────────────────────────────────
  async function handleAdd() {
    const name = newGroupName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await saveCatalogGroup({ connectionId, groupName: name });
      toast.success(`分组「${name}」已创建`);
      setNewGroupName('');
      onGroupsChange();
    } catch {
      toast.error('创建分组失败');
    } finally {
      setAdding(false);
    }
  }

  // ── Rename group ────────────────────────────────────────────────
  function startRename(group: CatalogGroupItem) {
    setEditingId(group._id);
    setEditingName(group.group_name);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }

  async function commitRename(group: CatalogGroupItem) {
    const name = editingName.trim();
    if (!name || name === group.group_name) {
      setEditingId(null);
      return;
    }
    try {
      await saveCatalogGroup({ groupId: group._id, connectionId, groupName: name });
      toast.success(`分组已重命名为「${name}」`);
      onGroupsChange();
    } catch {
      toast.error('重命名失败');
    } finally {
      setEditingId(null);
    }
  }

  // ── Sort order ──────────────────────────────────────────────────
  function startSortEdit(group: CatalogGroupItem) {
    setEditingSortId(group._id);
    setEditingSortValue(group.sort_order);
  }

  async function commitSortOrder(group: CatalogGroupItem) {
    if (editingSortValue === group.sort_order) {
      setEditingSortId(null);
      return;
    }
    try {
      await saveCatalogGroup({
        groupId: group._id,
        connectionId,
        groupName: group.group_name,
        sortOrder: editingSortValue,
      });
      toast.success('排序已更新');
      onGroupsChange();
    } catch {
      toast.error('更新排序失败');
    } finally {
      setEditingSortId(null);
    }
  }

  // ── Delete group ────────────────────────────────────────────────
  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteCatalogGroup({ groupId: deleteTarget._id });
      toast.success(
        `分组「${deleteTarget.group_name}」已删除，${res.tables_ungrouped} 张表回归未分类`,
      );
      setDeleteTarget(null);
      onGroupsChange();
    } catch {
      toast.error('删除分组失败');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="w-72 space-y-2 p-3">
      <h4 className="text-sm font-semibold">分组管理</h4>

      {/* ── Group list ─────────────────────────────────────────── */}
      {groups.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">暂无分组</p>
      )}

      <ul className="space-y-1 max-h-64 overflow-y-auto">
        {groups.map((g) => (
          <li
            key={g._id}
            className="flex items-center gap-1.5 rounded px-2 py-1 hover:bg-accent text-sm group"
          >
            <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

            {/* Name — inline edit or display */}
            {editingId === g._id ? (
              <Input
                ref={editInputRef}
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={() => commitRename(g)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename(g);
                  if (e.key === 'Escape') setEditingId(null);
                }}
                className="h-6 text-sm flex-1 min-w-0"
              />
            ) : (
              <span className="flex-1 truncate">{g.group_name}</span>
            )}

            {/* Table count badge */}
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
              {g.table_count}
            </Badge>

            {/* Sort order — inline edit or display */}
            {editingSortId === g._id ? (
              <Input
                type="number"
                value={editingSortValue}
                onChange={(e) => setEditingSortValue(Number(e.target.value))}
                onBlur={() => commitSortOrder(g)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitSortOrder(g);
                  if (e.key === 'Escape') setEditingSortId(null);
                }}
                className="h-6 w-12 text-xs text-center shrink-0"
              />
            ) : (
              <span
                className="text-xs text-muted-foreground w-6 text-center cursor-pointer shrink-0"
                title="点击编辑排序"
                onClick={() => startSortEdit(g)}
              >
                {g.sort_order}
              </span>
            )}

            {/* Action buttons */}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
              onClick={() => startRename(g)}
              title="重命名"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive shrink-0"
              onClick={() => setDeleteTarget(g)}
              title="删除"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        ))}
      </ul>

      {/* ── Add group ──────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 pt-1 border-t">
        <Input
          placeholder="新分组名称"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
          className="h-7 text-sm flex-1"
        />
        <Button
          size="sm"
          className="h-7 px-2.5 text-xs"
          disabled={!newGroupName.trim() || adding}
          onClick={handleAdd}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          添加
        </Button>
      </div>

      {/* ── Delete confirmation dialog ─────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>删除分组</DialogTitle>
            <DialogDescription>
              确定删除分组「{deleteTarget?.group_name}」？该分组下的表将自动回归未分类。
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>
              取消
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? '删除中…' : '确定删除'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
